import React, { useCallback, useEffect, useState, useMemo } from 'react';
import cx from 'classnames';
import Col from '../../../../../../components/GridSystem/Col';
import Row from '../../../../../../components/GridSystem/Row';
import Ref from '../../../../../../components/Ref/Ref';
import Content from '../../../../../../components/Content/Content';
import styles from './ReviewDashboard.module.scss';
import ReviewCards from '../ReviewCards/ReviewCards';
import MobileHeader from '../../../../../../components/MobileHeader';
import { useResponsiveContext } from '../../../../../../context/ResponsiveContext';
import useFetchListWithInfiniteScroll from '../../../../../../hooks/useFetchListWithInfiniteScroll';
import VideoService from '../../../../../../service/VideoService';
import { useLibraryContext } from '../../../../../../context/LibraryContext';
import RightSidebarMenu from '../RightSidebarMenu/RightSidebarMenu';
import ContentPageHeader from '../../../../../../components/ContentPageHeader/ContentPageHeader';
import RecordNotFound from '../../../../../../components/_empty_state/RecordNotFound';
import LocalStorageService from '../../../../../../service/LocalStorageService';
import { useUserContext } from '../../../../../../context/UserContext';
import { LoadingSpinner } from '../../../../../../components/Icon/icons';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { approvalStatusesForReviewer } from '../../../VideosFilter/constants';
import VideosFilter from '../../../VideosFilter/VideosFilter';
import MobileSearchModal from '../../../../../../components/MobileSearchModal/MobileSearchModal';
import SearchModalContent from '../../../../../../components/SearchModalContent/SearchModalContent';

const viewModeToken = { GRID_VIEW: 'GRID_VIEW', LIST_VIEW: 'LIST_VIEW' };
const viewModeInit = viewModeToken.GRID_VIEW;

const Review = () => {
  const [viewMode, setViewMode] = useState(viewModeInit);
  const isListView = useMemo(() => viewMode === viewModeToken.LIST_VIEW, [viewMode]);

  const { user, isApprovalFlowEnabled, isReviewerUser, appConfig } = useUserContext();
  const { isMdAndBelow } = useResponsiveContext();
  const { selectedCard, toggleUnselectCards } = useLibraryContext();
  const [searchModal, setSearchModal] = useState(false);
  const selectedVideoCount = Object.keys(selectedCard).length;

  const [searchValue, setSearchValue] = useState('');

  const { state } = useLocation();

  const { t } = useTranslation();

  const [selectedFilter, setSelectedFilter] = useState({
    TYPE: approvalStatusesForReviewer.IN_REVIEW.TYPE,
    DESCRIPTION: approvalStatusesForReviewer.IN_REVIEW.DESCRIPTION,
  });

  const history = useHistory();

  useEffect(() => {
    // Check app config
    if (!appConfig) {
      return;
    }

    let appConfigFeatures = appConfig.features;
    if (appConfigFeatures) {
      const featureWebAccess = appConfigFeatures.find(feature => feature.name === 'web_access');
      if (featureWebAccess) {
        if (!featureWebAccess.enabled) {
          history.push('/logout', { web_access_error: true });
          return;
        }
      }
    }

    if (!isReviewerUser || !isApprovalFlowEnabled) {
      history.push('/eg/home');
    }
  }, [appConfig]);

  useEffect(() => {
    const savedViewMode = LocalStorageService.getItem('ViewMode');

    if (savedViewMode) {
      setViewMode(savedViewMode);
    }

    const isiOS =
      !navigator.userAgent.match('CriOS') &&
      (/iPad|iPhone|iPod/.test(navigator.platform) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) &&
      !window.MSStream;

    function handleiIOSPopstate() {
      window.location.reload();
    }

    isiOS && window.addEventListener('popstate', handleiIOSPopstate);

    return () => {
      isiOS && window.removeEventListener('popstate', handleiIOSPopstate);
    };
  }, []);

  const getVideosWithStats = useCallback(async query => {
    query.folder = null;
    query.reviewing = true;

    if (!query.approvalStatus) {
      query.approvalStatus = approvalStatusesForReviewer.IN_REVIEW.TYPE;
    }

    try {
      const response = await VideoService.getVideos(query);

      return new Promise(function (resolve) {
        return resolve(response);
      });
    } catch (err) {
      return new Promise(function (resolve, reject) {
        return reject(err);
      });
    }
  }, []);

  const {
    infiniteRef,
    list: videos,
    setList: setVideos,
    totalData,
    totalCurrent,
    isEmptyList: isVideosEmpty,
    loadList: loadVideos,
    isSearching,
    loading,
    searchInput: searchInputVideos,
    query,
    setTotalData,
    onApprovalStatusFilter,
  } = useFetchListWithInfiniteScroll({ service: getVideosWithStats });

  const onInputChange = value => {
    setSearchValue(value);
    searchInputVideos.onChange(value);
  };

  const closeSearchModal = () => {
    setSearchModal(false);
  };

  const openSearchModal = () => {
    setSearchModal(true);
  };

  const handleSetListView = useCallback(() => {
    if (!isListView) {
      setViewMode(viewModeToken.LIST_VIEW);
      LocalStorageService.setItem('ViewMode', viewModeToken.LIST_VIEW);
    }
  }, [isListView]);

  const handleSetGridView = useCallback(() => {
    if (isListView) {
      setViewMode(viewModeToken.GRID_VIEW);
      LocalStorageService.setItem('ViewMode', viewModeToken.GRID_VIEW);
    }
  }, [isListView]);

  const setVideosAndTotalData = useCallback(
    updatedVideos => {
      setVideos(updatedVideos);
      setTotalData(totalData - 1);
    },
    [setTotalData, setVideos, totalData]
  );

  const handleChangeApprovalFilter = value => {
    if (selectedFilter.TYPE === value.TYPE) {
      return;
    }

    setVideos([]);
    setTotalData(0);
    setSelectedFilter(value);
    onApprovalStatusFilter(value.TYPE);
    toggleUnselectCards();
    history.replace({ state: { ...state, filter: value } });
  };

  useEffect(() => {
    if (state?.filter !== null && state?.filter !== undefined) {
      handleChangeApprovalFilter(state?.filter);
    }
  }, []);

  return (
    <>
      <MobileHeader
        libraryMode={true}
        userAvatar={user?.avatarUri}
        userFirstLetter={user?.userFullName.charAt(0).toUpperCase()}
        openSearchModal={openSearchModal}
      />
      <Content className={styles.content}>
        <div className={styles.wrapper}>
          <ContentPageHeader
            isMdAndBelow={isMdAndBelow}
            isListView={isListView}
            handleSetGridView={handleSetGridView}
            handleSetListView={handleSetListView}
            searchValue={searchValue}
            handleInputChange={onInputChange}
            title={t('review.title')}
            mode="REVIEW"
          />
          {/* <Tabs state={tab} onClick={handleClickTab} /> */}
          <div className={styles.reviewHeader}>
            {loading ? (
              <h2>{t('general.loading')}</h2>
            ) : (
              !(query.searchTerm && videos?.length === 0 && !isVideosEmpty) && (
                <>
                  <h2>
                    {t('dashboard.all_videos')} ({videos && totalData})
                  </h2>
                  <VideosFilter
                    selectedItem={selectedFilter}
                    data={approvalStatusesForReviewer}
                    onClick={handleChangeApprovalFilter}
                  />
                </>
              )
            )}
          </div>
          <Col xs={12} className={cx(styles.videoLibrary)}>
            <Ref innerRef={infiniteRef}>
              <Row className={styles.row}>
                {(!loading || videos.length > 0) && (
                  <ReviewCards
                    searchInput={searchValue}
                    videos={videos}
                    listMode={isListView}
                    setVideosAndTotalData={setVideosAndTotalData}
                    loadVideos={loadVideos}
                    searchModal={searchModal}
                    closeSearchModal={closeSearchModal}
                    selectedFilter={selectedFilter}
                  />
                )}
                {loading && videos.length > 0 && (
                  <div className={styles.spinnerContainer}>
                    <LoadingSpinner theme="light" />
                  </div>
                )}
              </Row>
            </Ref>
          </Col>
          {!loading && query.searchTerm && videos?.length === 0 && !isVideosEmpty && <RecordNotFound searchTerm={query.searchTerm} />}
        </div>
        {!!selectedVideoCount && (
          <RightSidebarMenu
            isPendingForReview={selectedFilter.TYPE === approvalStatusesForReviewer.IN_REVIEW.TYPE}
            isApproved={selectedFilter.TYPE === approvalStatusesForReviewer.APPROVED.TYPE}
          />
        )}
        <MobileSearchModal open={searchModal} onClose={closeSearchModal}>
          <SearchModalContent onClose={closeSearchModal} videos={videos} selectedFilter={selectedFilter} isReview />
        </MobileSearchModal>
      </Content>
    </>
  );
};

export default Review;
